import { Field } from '@headlessui/react';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import MinusIcon from '../../../../../../assets/icons/minus-icon';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { Checkbox } from '../../../../../../components/checkbox/index';
import Loader from '../../../../../../components/loader/index';
import { PaginationController } from '../../../../../../components/pagination-controller/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import {
  SafeHooks,
  ScenarioHooks,
  useDebounce,
  useFormat,
  usePagination,
} from '../../../../../../hooks/index';
import { useDefaultShareClass } from '../../../../../../hooks/share-class/index';
import { Safe, safeCapTypeTitle } from '../../../../../../types/safes.types';
import { toRound } from '../../../../../../utils/get-rounded-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { FundraisingFormSchema } from '../../../fundraising-validation';
import { TableHeaderItem } from '../form-components/index';
import { FormShareClassCombobox } from '../form-components/share-class/form/index';
import { ShareClassCombobox } from '../form-components/share-class/index';
import { ShareClassValue } from '../form-components/share-class/share-class.types';
import { useSafeStore } from '../safe.store';
import { useClassConversionPendingStore } from './items.store';
import { ExistingItemListProps, itemsToFetchMap } from './items.types';

export type SafeItemProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  data: { capitalOwned: number; postRoundCapitalOwned: number; sharesConverted: number } | null;
  isChecked: boolean;
  item: Safe;
  scenarioId: string;
};

export const SafeItem: FC<SafeItemProps> = ({
  isChecked,
  companyId,
  control,
  item,
  scenarioId,
  data,
}) => {
  const { discount, floorValue, id, investment, capValue, capType, stakeholder, maturityDate } =
    item;
  const { format } = useFormat();
  const { safes } = useWatch<FundraisingFormSchema>({ control });
  const safeItem = safes?.safeItems?.[id];
  const [isSelected, setSelected] = useState(isChecked ?? false);
  const { setIsPending: setIsClassConversionPending } = useClassConversionPendingStore();
  const { scheduleUpdate } = useScenarioCapTableStore();

  const { pointerBlockedStyles } = useScenarioStore();

  const { decreaseSelectedCount, increaseSelectedCount } = useSafeStore();
  const { shareClass: defaultShareClass } = useDefaultShareClass();

  const [shareClass, setShareClass] = useState<null | ShareClassValue>(
    safeItem?.shareClass?.id && safeItem?.shareClass?.name
      ? { id: safeItem.shareClass.id, name: safeItem.shareClass.name }
      : { id: defaultShareClass?.id || '', name: defaultShareClass?.name || '' },
  );

  const { createScenarioSafe } = ScenarioHooks.useCreateScenarioSafe();
  const { deleteScenarioSafe } = ScenarioHooks.useDeleteScenarioSafe();
  const { updateScenarioSafe, isPending } = ScenarioHooks.usePatchScenarioSafe();

  const handleSelect = () => {
    if (shareClass?.id) {
      setSelected(true);
      increaseSelectedCount(scenarioId);
      createScenarioSafe(
        {
          scenarioId,
          data: { safeId: id, shareClassId: shareClass.id },
        },
        {
          onSuccess: () => scheduleUpdate(scenarioId),
          // Refused (e.g. the SAFEs would own 100 % or more): it was not added, so untick it.
          // The scenario is unchanged, so "Update Scenario" stays as it was.
          onError: () => {
            setSelected(false);
            decreaseSelectedCount(scenarioId);
          },
        },
      );
    }
  };

  const handleRemove = () => {
    scheduleUpdate(scenarioId);
    setSelected(false);
    decreaseSelectedCount(scenarioId);
    deleteScenarioSafe({ scenarioId, id });
  };

  const { debounceCallback } = useDebounce<{ shareClass: { id: string } }>({
    debounce: 300,
    callback: ({ shareClass: { id: shareClassId } }) => {
      scheduleUpdate(scenarioId);

      updateScenarioSafe({
        scenarioId,
        id,
        data: { shareClassId, safeId: safeItem?.safeId },
      });
    },
  });

  useEffect(() => {
    setSelected(isChecked ?? false);
  }, [isChecked]);

  useEffect(() => {
    if (isPending) {
      setIsClassConversionPending(true);
    } else {
      const timeout = setTimeout(() => {
        setIsClassConversionPending(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isPending, setIsClassConversionPending]);
  return (
    <Fragment>
      <div
        className={twJoin('flex cursor-pointer items-center justify-center', pointerBlockedStyles)}
      >
        <Field className="flex items-center">
          <Checkbox
            checked={isSelected}
            disabled={!shareClass?.id}
            id={id}
            onChange={(state) => {
              state ? handleSelect() : handleRemove();
            }}
          />
        </Field>
      </div>
      <div className="px-2 py-[26px]">
        <Tooltip childrenWrapperClassName="max-w-[300px] pt-[2px]" content={stakeholder?.fullName}>
          <span className="w-full truncate text-nowrap text-sm font-[450] text-gray-700">
            {stakeholder?.fullName}
          </span>
        </Tooltip>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {investment?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {capValue?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-'}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">{toRound(discount * 100)}%</span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {floorValue?.toLocaleString('en-US')}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {maturityDate ? format(maturityDate, 'dd/MM/yyyy') : '-'}
        </span>
      </div>
      <div className={twJoin('flex items-center px-2 py-4', pointerBlockedStyles)}>
        {safeItem ? (
          <ShareClassCombobox
            companyId={companyId}
            control={control}
            name={`safes.safeItems.${id}.shareClass`}
            onChange={(shareClass) => {
              debounceCallback({ shareClass });
              setShareClass(shareClass);
            }}
          />
        ) : (
          <FormShareClassCombobox
            companyId={companyId}
            defaultValue={shareClass}
            setShareClass={(shareClass) => {
              setShareClass(shareClass);
            }}
          />
        )}
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {data?.capitalOwned ? `${toRound(data.capitalOwned * 100, 2)}%` : '-'}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {data?.postRoundCapitalOwned ? `${toRound(data.postRoundCapitalOwned * 100, 2)}%` : '-'}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {data?.sharesConverted ? data?.sharesConverted?.toLocaleString('en-US') : '-'}
        </span>
      </div>
    </Fragment>
  );
};

export const SafeItemList: FC<ExistingItemListProps> = ({
  control,
  companyId,
  scenarioId,
  columns,
}) => {
  const [currentPage, setCurrentPage] = usePagination(`scenario-safes:${scenarioId}`);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const { deselectAll, selectAll, scenarioStates } = useSafeStore();
  const safesStore = scenarioStates[scenarioId] ?? null;

  const pointerBlockedStyles = useScenarioStore(({ pointerBlockedStyles }) => pointerBlockedStyles);

  const { safes: items = [], totalPages } = SafeHooks.useSafes({
    sortOrder: null,
    sortBy: null,
    companyId,
    numberOfFetchedItems: Number(itemsToFetch),
    currentPage,
  });

  const { data: calculatedSafes, isLoading } = ScenarioHooks.useCalcScenarioSafes({
    scenarioId,
    enabled: !pointerBlockedStyles,
  });

  const { scheduleUpdate } = useScenarioCapTableStore();

  const { shareClass: defaultShareClass } = useDefaultShareClass();

  const { deleteScenarioSafes, isPending: isPendingDeletion } =
    ScenarioHooks.useDeleteAllScenarioSafes();

  const { selectAllSafes, isPending } = ScenarioHooks.useSelectAllScenarioSafes();

  const onSelectAll = () => {
    selectAll(scenarioId, items?.length);
    selectAllSafes(
      { scenarioId, data: { shareClassId: defaultShareClass?.id || '' } },
      {
        onSuccess: () => scheduleUpdate(scenarioId),
        // Refused as a group (e.g. owning 100 % or more): none was added.
        onError: () => deselectAll(scenarioId),
      },
    );
  };

  const onRemoveSelectAll = () => {
    deselectAll(scenarioId);
    scheduleUpdate(scenarioId);

    deleteScenarioSafes({ scenarioId });
  };

  return (
    <div
      className={twMerge(
        'relative flex w-full min-w-[1520px] flex-col items-end gap-3',
        isPending || isPendingDeletion ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {(isPending || isPendingDeletion) && <Loader className="absolute z-10" />}
      {items && items.length > 0 && (
        <div
          className={twMerge(
            'flex min-w-[1520px] flex-col rounded-lg border-[1px] border-gray-100 bg-white',
            isLoading && 'pointer-events-none animate-pulse bg-gray-50/50',
          )}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns: `48px repeat(10, minmax(130px, 1fr)) 190px`,
            }}
          >
            <div
              className={twJoin(
                'relative flex h-full w-full items-center justify-center border-b-[1px] border-b-gray-100 bg-gray-50 px-4',
                pointerBlockedStyles,
              )}
            >
              <Field className="flex cursor-pointer items-center">
                <Checkbox
                  checked={safesStore?.isSelectedAll || !!calculatedSafes?.[0]}
                  id="select-all"
                  onChange={(checked) => (checked ? onSelectAll() : onRemoveSelectAll())}
                  selectIcon={<MinusIcon />}
                />
              </Field>
            </div>
            {columns?.map((column, index) => (
              <TableHeaderItem key={index} text={column} />
            ))}
          </div>
          {items.map((item) => {
            const calculatedSafe =
              calculatedSafes?.find(({ safeId }) => safeId === item.id) || null;

            return (
              <div
                className="grid border-b-[1px] border-b-gray-100"
                key={calculatedSafe?.safeId || `${item.id}_un_calculated`}
                style={{
                  gridTemplateColumns: `48px repeat(10, minmax(130px, 1fr)) 190px`,
                }}
              >
                <SafeItem
                  companyId={companyId}
                  control={control}
                  data={calculatedSafe}
                  isChecked={safesStore?.isSelectedAll || !!calculatedSafe}
                  item={item as Safe}
                  scenarioId={scenarioId}
                />
              </div>
            );
          })}
        </div>
      )}
      <PaginationController
        currentPage={currentPage}
        itemsToFetch={itemsToFetch}
        itemsToFetchMap={itemsToFetchMap}
        onClick={setCurrentPage}
        onFetchAmountChange={setItemsToFetch}
        totalPages={totalPages || 1}
      />
    </div>
  );
};
